/**
 * A lista de compras: uma só, sempre viva.
 *
 * "Acabou o leite" entra, "comprei" sai. A mãe abre isto de pé no corredor do
 * mercado, com o carrinho numa mão, para saber o que falta **agora** — então a
 * lista é uma coluna de linhas grandes, sem cartão, sem cabeçalho de seção e
 * sem nada para ler antes de agir.
 *
 * Nada aqui conta: sem "3 de 8", sem porcentagem da lista, sem "faltou você
 * comprar". Comprar não é tarefa cumprida, é coisa que sai da lista.
 *
 * O que foi comprado nas últimas doze horas fica embaixo, só para desfazer o
 * toque errado — não é histórico, e a linha "some daqui sozinho" está lá para
 * a mãe não achar que a Ruth guarda o que ela compra.
 *
 * **Comprado e apagado não são a mesma coisa, e confundir as duas custa caro.**
 * Comprado é uma coisa que aconteceu: sai da lista, espera umas horas embaixo e
 * volta com um toque. Apagar é a mãe dizendo que a linha não devia existir — a
 * Ruth entendeu errado, ou ela mudou de ideia — e aí some de vez. Era a única
 * coisa da casa que só sabia crescer: um "leit" digitado com o dedo errado
 * ficava ali, ruído em toda ida ao mercado, até alguém comprar. É a regra 7,
 * e o conserto mora na linha onde ela vê o erro.
 *
 * As duas ações moram longe uma da outra de propósito. A linha inteira continua
 * sendo "comprei", que é o que ela faz cem vezes; o conserto abre pela seta do
 * fim, que é o mesmo sinal de "dá pra mexer" da tela A casa; e o apagar só
 * aparece dentro do conserto, como link discreto e com uma pergunta antes. Se
 * apagar tivesse o peso de riscar, ela apagaria querendo riscar — e perderia
 * justamente o desfazer que esta tela promete.
 *
 * **Não existe mockup desta tela em docs/design.** O desenho é derivado da
 * linguagem de Casa.dc.html (a linha com divisor fino, o convite tracejado) e
 * de Main.dc.html (o cartão de compras), e precisa de revisão do Carlos.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'

import {
  api,
  fraseDoErro,
  type Compras as ListaDeCompras,
  type CorrecaoDeItem,
  type ItemDeCompra,
} from '../api'
import { Icone } from '../componentes/Icone'
import '../estilos/conserto.css'
import '../estilos/compras.css'

type Props = {
  /**
   * Acrescentar mora fora daqui: a rota é nova e `api.ts` está com outro
   * agente. A tela recarrega do servidor depois, porque quem sabe a ordem da
   * lista — e se aquele nome já estava nela — é ele.
   */
  aoAcrescentar: (nome: string) => Promise<void>
}

export function Compras({ aoAcrescentar }: Props) {
  const [lista, setLista] = useState<ListaDeCompras | null>(null)
  const [rascunho, setRascunho] = useState('')
  const [escrevendo, setEscrevendo] = useState(false)
  /** Qual linha está aberta para conserto. Uma de cada vez. */
  const [corrigindo, setCorrigindo] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const campo = useRef<HTMLInputElement>(null)

  const carregar = useCallback(async () => {
    try {
      setLista(await api.compras())
      setErro(null)
    } catch (e) {
      setErro(fraseDoErro(e, 'não consegui abrir sua lista'))
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  /**
   * O item sai da lista no toque, antes da resposta do servidor: ela está em
   * pé no mercado, e esperar a rede para riscar o leite faria ela tocar duas
   * vezes. Se o servidor recusar, a lista volta a ser a dele.
   */
  const comprar = useCallback(
    async (item: ItemDeCompra) => {
      setLista(
        (antes) =>
          antes && {
            itens: antes.itens.filter((i) => i.id !== item.id),
            comprados: [item, ...antes.comprados],
          },
      )
      try {
        await api.comprar(item.id)
        setErro(null)
      } catch (e) {
        setErro(fraseDoErro(e, 'não consegui riscar isso agora'))
        await carregar()
      }
    },
    [carregar],
  )

  /**
   * Desfazer recarrega em vez de adivinhar: o item volta para o lugar em que
   * estava na lista, e esse lugar é a ordem de chegada, que só o servidor tem.
   */
  const devolver = useCallback(
    async (item: ItemDeCompra) => {
      try {
        await api.devolver(item.id)
        await carregar()
      } catch (e) {
        setErro(fraseDoErro(e, 'não consegui trazer isso de volta'))
      }
    },
    [carregar],
  )

  /**
   * O erro sobe para o formulário, e não para o alto da tela: quem tentou
   * salvar está olhando para os campos, e numa lista comprida a frase lá em
   * cima é invisível para quem tocou lá embaixo.
   *
   * A rota devolve o item inteiro, então a linha é trocada pelo que o servidor
   * respondeu — é isso que faz "leite" aparecer ali mesmo, no lugar do "leit",
   * em vez de uma esperança de que tenha dado certo. E a linha fica onde
   * estava: recarregar reordenaria a lista debaixo do dedo dela.
   */
  const corrigir = useCallback(async (item: ItemDeCompra, campos: CorrecaoDeItem) => {
    const novo = await api.corrigirCompra(item.id, campos)
    setLista(
      (antes) =>
        antes && { ...antes, itens: antes.itens.map((i) => (i.id === novo.id ? novo : i)) },
    )
    setCorrigindo(null)
  }, [])

  /**
   * Apagar não passa por "comprado": some da tela na hora e não volta.
   *
   * Sem recarregar de propósito — ela acabou de dizer que essa linha não devia
   * existir, e uma ida ao servidor só para confirmar deixaria o erro mais um
   * instante na frente dela.
   */
  const apagar = useCallback(async (item: ItemDeCompra) => {
    await api.apagarCompra(item.id)
    setLista(
      (antes) => antes && { ...antes, itens: antes.itens.filter((i) => i.id !== item.id) },
    )
    setCorrigindo(null)
  }, [])

  const acrescentar = useCallback(
    async (evento: FormEvent) => {
      evento.preventDefault()
      const nome = rascunho.trim()
      if (!nome || escrevendo) return

      setEscrevendo(true)
      try {
        await aoAcrescentar(nome)
        setRascunho('')
        setErro(null)
        await carregar()
      } catch (e) {
        // O que ela escreveu fica no campo: digitar de novo seria castigo.
        setErro(fraseDoErro(e, 'não consegui pôr isso na lista'))
      } finally {
        setEscrevendo(false)
        // Ela costuma lembrar de três coisas seguidas; o cursor espera a próxima.
        campo.current?.focus()
      }
    },
    [aoAcrescentar, carregar, escrevendo, rascunho],
  )

  const itens = lista?.itens ?? []
  const comprados = lista?.comprados ?? []

  return (
    <>
      <header className="cabeca">
        <div className="cabeca__datas">
          <h1 className="cabeca__titulo">Compras</h1>
          <span className="cabeca__data">o que falta em casa</span>
        </div>
      </header>

      <div className="corpo">
        {erro && <p className="vazio">{erro}</p>}

        {lista && itens.length === 0 && (
          <p className="vazio">Não falta nada. Quando acabar alguma coisa, conta pra Ruth.</p>
        )}

        {itens.length > 0 && (
          <div className="compra-lista">
            {itens.map((item) => (
              <Falta
                key={item.id}
                item={item}
                corrigindo={corrigindo === item.id}
                aoComprar={() => void comprar(item)}
                aoAbrir={() => setCorrigindo(item.id)}
                aoFechar={() => setCorrigindo(null)}
                aoCorrigir={(campos) => corrigir(item, campos)}
                aoApagar={() => apagar(item)}
              />
            ))}
          </div>
        )}

        {/* O campo fica no fim da lista, e é campo, não formulário: um nome,
            uma tecla. Quantidade, dia e quem compra são conversa com a Ruth. */}
        <form className="compra-novo" onSubmit={(evento) => void acrescentar(evento)}>
          <input
            ref={campo}
            className="compra-novo__campo"
            value={rascunho}
            onChange={(evento) => setRascunho(evento.target.value)}
            placeholder="O que mais falta?"
            aria-label="Acrescentar o que falta"
            maxLength={120}
            autoComplete="off"
          />
          <button
            type="submit"
            className="compra-novo__botao"
            aria-label="Pôr na lista"
            disabled={!rascunho.trim() || escrevendo}
          >
            <Icone nome="somar" tamanho={22} />
          </button>
        </form>

        {comprados.length > 0 && (
          <div className="compra-comprados">
            <span className="eyebrow">Comprado</span>
            <p className="compra-aviso">Some daqui sozinho.</p>
            <div className="compra-lista">
              {comprados.map((item) => (
                <Comprado key={item.id} item={item} aoVoltar={() => void devolver(item)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/**
 * Uma linha do que falta. O toque continua sendo a linha inteira: a mão dela
 * está ocupada, e comprar é o que ela faz cem vezes para cada conserto.
 *
 * A seta do fim é alvo separado, e é a única porta do conserto. Fica na ponta
 * porque um alvo de conserto no meio da linha roubaria dedo do "comprei"; e é
 * seta, e não a palavra "Corrigir", porque a lista é para ela varrer com o
 * olho no corredor do mercado — uma palavra repetida em toda linha é uma
 * palavra para ler antes de agir. A seta já diz "dá pra mexer" na tela A casa
 * e na Semana, e quem ouve a tela recebe a frase inteira no rótulo.
 */
function Falta({
  item,
  corrigindo,
  aoComprar,
  aoAbrir,
  aoFechar,
  aoCorrigir,
  aoApagar,
}: {
  item: ItemDeCompra
  corrigindo: boolean
  aoComprar: () => void
  aoAbrir: () => void
  aoFechar: () => void
  aoCorrigir: (campos: CorrecaoDeItem) => Promise<void>
  aoApagar: () => Promise<void>
}) {
  // O conserto toma o lugar da linha em vez de abrir embaixo dela: enquanto ela
  // arruma o nome, a linha ali em cima continuaria sendo um botão "comprei" de
  // largura inteira — convite a marcar como comprado justo o que ela veio
  // consertar.
  if (corrigindo) {
    return (
      <Conserto item={item} aoCorrigir={aoCorrigir} aoApagar={aoApagar} aoFechar={aoFechar} />
    )
  }

  // O rótulo repete a quantidade porque ele substitui o texto visível para
  // quem ouve a tela — sem isto ela ouviria "leite" e compraria um só.
  const rotulo = item.quantidade
    ? `Comprei ${item.nome}, ${item.quantidade}`
    : `Comprei ${item.nome}`

  return (
    <div className="compra">
      {/* Sem `tocavel`: essa classe é a marca de "esta linha abre um conserto",
          e quem abre conserto aqui é a seta do lado. */}
      <button type="button" className="compra__toque" aria-label={rotulo} onClick={aoComprar}>
        <span className="compra__marca" />
        <span className="compra__nome">{item.nome}</span>
        {item.quantidade && <span className="compra__quantidade">{item.quantidade}</span>}
      </button>

      <button
        type="button"
        className="compra__corrigir tocavel"
        aria-label={`Corrigir ${item.nome}`}
        onClick={aoAbrir}
      >
        <span className="tocavel__seta">
          <Icone nome="seta" tamanho={16} />
        </span>
      </button>
    </div>
  )
}

/**
 * O conserto de um item, já preenchido com o que está na lista.
 *
 * Vem cheio de propósito: quase sempre ela quer mexer numa coisa só, e um
 * formulário em branco a obrigaria a redigitar o que já estava certo.
 *
 * **Manda só o que ela mudou**, como todo conserto do app: campo ausente fica
 * como está, e abrir para olhar e fechar não muda nada nem avisa ninguém.
 *
 * O apagar é link discreto, embaixo, e pergunta antes — a mesma forma da tela
 * A casa e dos blocos do dia. Nunca botão de primeira linha ao lado do
 * "comprei": as duas ações se parecem em palavra ("tira da lista") e não se
 * parecem em nada no resto. Comprado volta; apagado não.
 */
function Conserto({
  item,
  aoCorrigir,
  aoApagar,
  aoFechar,
}: {
  item: ItemDeCompra
  aoCorrigir: (campos: CorrecaoDeItem) => Promise<void>
  aoApagar: () => Promise<void>
  aoFechar: () => void
}) {
  const quantidadeOriginal = (item.quantidade ?? '').trim()

  const [nome, setNome] = useState(item.nome)
  const [quantidade, setQuantidade] = useState(quantidadeOriginal)
  const [perguntando, setPerguntando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando || !nome.trim()) return

    const campos: CorrecaoDeItem = {}
    const limpo = nome.trim()
    if (limpo !== item.nome) campos.nome = limpo

    // Esvaziar o campo é como ela tira a quantidade — e um campo vazio não
    // pode virar `quantidade: ''`, que a rota leria como "não mexi nisso".
    // Sem o `tirar_a_quantidade`, um "leite, 2 caixas" nunca mais voltaria a
    // ser só "leite", e ela ficaria com um número que já não é verdade.
    const quanto = quantidade.trim()
    if (quanto !== quantidadeOriginal) {
      if (quanto) campos.quantidade = quanto
      else campos.tirar_a_quantidade = true
    }

    // Abriu para olhar e fechou: nada muda, e ninguém precisa saber que ela
    // abriu.
    if (Object.keys(campos).length === 0) {
      aoFechar()
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      await aoCorrigir(campos)
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui salvar agora.'))
      setSalvando(false)
    }
  }

  const apagar = async () => {
    setErro(null)
    try {
      await aoApagar()
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui tirar agora.'))
      setPerguntando(false)
    }
  }

  return (
    <form className="conf-pessoa compra-conserto" onSubmit={salvar}>
      <label className="conf-pessoa__campo">
        <span>O que é</span>
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Leite"
          maxLength={120}
          autoComplete="off"
          autoFocus
        />
      </label>

      {/* Sem "Tirar a quantidade" ao lado, ao contrário da hora da tela A casa:
          lá o botão existe porque o "x" do campo de hora some no celular e a
          hora só sabia entrar. Aqui é campo de texto — apagar o que está
          escrito é o gesto que ela já conhece, e é o que a tela lê como tirar. */}
      <label className="conf-pessoa__campo">
        {/* Pergunta, como "Em alguma hora certa?" da tela A casa: é assim que
            os formulários daqui dizem "só se você quiser". */}
        <span>Alguma quantidade?</span>
        <input
          value={quantidade}
          onChange={(e) => setQuantidade(e.target.value)}
          placeholder="2 caixas"
          maxLength={40}
          autoComplete="off"
        />
      </label>

      {erro && (
        <p className="conf-erro" role="alert">
          {erro}
        </p>
      )}

      {perguntando ? (
        <>
          <p className="conf-secao__nota">
            Comprado fica embaixo umas horas e dá pra voltar. Isto some de vez — é pro que não
            era pra estar na lista.
          </p>
          <div className="conf-pessoa__acoes">
            <button type="button" className="conf-pessoa__tirar" onClick={() => void apagar()}>
              Pode tirar
            </button>
            <button
              type="button"
              className="conf-pessoa__deixar"
              onClick={() => setPerguntando(false)}
            >
              Deixa aí
            </button>
          </div>
        </>
      ) : (
        <div className="conf-pessoa__acoes">
          <button type="submit" className="botao-primario" disabled={salvando || !nome.trim()}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
            Deixa como está
          </button>
          <button
            type="button"
            className="conf-pessoa__tirar-link"
            onClick={() => setPerguntando(true)}
          >
            Isso não faz falta
          </button>
        </div>
      )}
    </form>
  )
}

/**
 * Uma linha do que já foi comprado. Aqui a linha não é botão: o único toque
 * que sobra é o de desfazer, e ele precisa de alvo próprio para não ser dado
 * sem querer de novo.
 *
 * E não tem conserto nem apagar: o que está aqui já saiu da lista sozinho daqui
 * a algumas horas. Um "Corrigir" numa linha que está de saída seria trabalho
 * que se apaga junto, e um "apagar" a um dedo do "voltar" seria a confusão que
 * esta tela inteira foi desenhada para evitar.
 */
function Comprado({ item, aoVoltar }: { item: ItemDeCompra; aoVoltar: () => void }) {
  return (
    <div className="compra compra--comprado">
      <span className="compra__marca compra__marca--feita">
        <Icone nome="check" tamanho={14} />
      </span>
      <span className="compra__nome">{item.nome}</span>
      <button
        type="button"
        className="compra__voltar"
        aria-label={`Devolver ${item.nome} para a lista`}
        onClick={aoVoltar}
      >
        voltar
      </button>
    </div>
  )
}
